import { type Edge, type OnEdgesChange, applyEdgeChanges } from '@xyflow/react'
import { useCallback, useEffect, useState } from 'react'
import * as Y from 'yjs'
// Please see the comments in useNodesStateSynced.ts.
// This is the same thing but for edges.
//export const edgesMap = ydoc.getMap<Edge>('edges')

export function initializeEdgesStateSynced(ydoc: Y.Doc) {
  const edgesMap = ydoc.getMap<Edge>('edgesMap')

  return function useEdgesStateSynced(): [
    Edge[],
    React.Dispatch<React.SetStateAction<Edge[]>>,
    OnEdgesChange,
  ] {
    const [edges, setEdges] = useState<Edge[]>([])

    const setEdgesSynced = useCallback(
      (edgesOrUpdater: React.SetStateAction<Edge[]>) => {
        /* console.log(
          '[useEdgesStateSynced.setEdgesSynced].edgesOrUpdater.',
          edgesOrUpdater
        ) */
        const next =
          typeof edgesOrUpdater === 'function'
            ? edgesOrUpdater([...edgesMap.values()])
            : edgesOrUpdater

        const seen = new Set<string>()

        for (const edge of next) {
          if (edge && edge.id) {
            seen.add(edge.id)
            edgesMap.set(edge.id, edge)
          }
        }

        for (const edge of edgesMap.values()) {
          if (edge && edge.id && !seen.has(edge.id)) {
            edgesMap.delete(edge.id)
          }
        }
      },
      []
    )

    const onEdgesChange: OnEdgesChange = useCallback((changes) => {
      /* console.log('[useEdgesStateSynced.onEdgesChange].changes.', changes) */
      const edges = Array.from(edgesMap.values()).filter(
        (edge) => edge !== undefined
      )
      const nextEdges = applyEdgeChanges(changes, edges)

      for (const change of changes) {
        if (change.type === 'add' || change.type === 'replace') {
          edgesMap.set(change.item.id, change.item)
        } else if (change.type === 'remove' && edgesMap.has(change.id)) {
          edgesMap.delete(change.id)
        } else {
          edgesMap.set(change.id, nextEdges.find((n) => n.id === change.id)!)
        }
      }
    }, [])

    useEffect(() => {
      const observer = () => {
        /* console.log(
          '[useEdgesStateSynced.useEffectobserver].edgesMap.values().',
          Array.from(edgesMap.values())
        ) */
        setEdges(
          Array.from(edgesMap.values()).filter((edge) => edge !== undefined)
        )
      }

      setEdges(
        Array.from(edgesMap.values()).filter((edge) => edge !== undefined)
      )
      edgesMap.observe(observer)

      return () => edgesMap.unobserve(observer)
    }, [setEdges])

    return [edges, setEdgesSynced, onEdgesChange]
  }
}
